import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Info } from 'lucide-react';
import Footer from '@/components/Footer';

const FAQPage = () => {
  const faqs = [
    {
      question: "How does Cittasya® contribute to wellness?",
      answer: "Wellness is a lifestyle that promotes holistic health and vitality. As multidimensional beings, true well-being requires nurturing our physical, energetic, mental, emotional, and spiritual aspects. Yoga Nidra supports this by rejuvenating and harmonizing all layers of the self, fostering optimal health and helping us truly thrive."
    },
    {
      question: "What is Yoga Nidra?",
      answer: "Yoga Nidra is a powerful technique where one learns to relax consciously and counteract stress. It is known as psychic sleep where the awareness is held at the threshold of wakefulness and sleep. The practice provides a blissful and refreshing experience."
    },
    {
      question: "How long does each practice session take?",
      answer: "Initially, sessions begin at 15-20 minutes and may gradually extend up to 35 minutes as one progresses through the stages."
    },
    {
      question: "Why should one practice Yoga Nidra?",
      answer: "Yoga Nidra teaches the art of proper relaxation. In the hypnagogic state of Yoga Nidra, the greatest amount of physical, emotional and mental tensions are released. In this state, as the consciousness is kept away from external sensory awareness and sleep, it becomes very powerful and can be used for deep relaxation, therapeutic applications, increased focus and memory, and reformed behaviour & personality."
    },
    {
      question: "How should one practice Yoga Nidra?",
      answer: "One should lie down in savasana on a yoga mat, undisturbed and still for the duration of the practice, while remaining awake and aware. The room should be sufficiently lit and ventilated. Avoid lying down directly under the running fan. If there are mobility issues in getting down on the floor, one may practice in a comfortable sitting position supported by props. Avoid practicing on the bed where you sleep."
    },
    {
      question: "When should we practice Yoga Nidra?",
      answer: "Try practicing at different times to find what works best for you – morning, evening or midday. Do not practice immediately after a meal."
    },
    {
      question: "What is the methodology used to progress in Yoga Nidra practice?",
      answer: "A scientific and systematic methodology is used to achieve profound relaxation by working through deep seated stressors. The Yoga Nidras follow a systematic approach, with stages building upon one another and progressively delving deeper. Each stage involves repeating the same practices for 7 days to progress to the next stage."
    },
    {
      question: "Which Yoga Nidra should I begin with?",
      answer: "It's recommended to begin with Yoga Nidra for Vitality."
    },
    {
      question: "Why do I need a sankalpa in the Yoga Nidra practice?",
      answer: "A sankalpa is a personal intention, a resolve you make to support your growth and highest truth. While you could target a specific goal like stopping an unhealthy habit, it's more effective to have a broader focus. This often leads to the desired behaviour change and brings additional benefits."
    },
    {
      question: "Who should not practice Yoga Nidra?",
      answer: "Individuals with severe mental disorders such as psychosis, schizophrenia, or major depression should avoid the practices listed on this platform."
    },
    {
      question: "Are there any contraindications in the practice of Yoga Nidra?",
      answer: "Yes. Pregnant women should avoid lying in savasana after 20 weeks. Instead, they should lie on the side, supported by cushions and pillows. Individuals suffering from post-traumatic stress disorder(PTSD) may find it helpful to lie on the side or stomach to feel safer and less vulnerable."
    },
    {
      question: "Is it common to experience certain sensations during Yoga Nidra practice, or not?",
      answer: "Yes. Yoga Nidra allows access to deeper layers of the subconscious where impressions are stored, often facilitating the release of blocked energy and emotions. Adopt a mindset of witnessing, allowing feelings to come to the surface and letting them float away without attachment or judgement. Some practitioners may not experience anything, and that's perfectly fine as well."
    },
    {
      question: "If I discontinue using the app or want to change my subscription plan, will the subscription amount be refunded?",
      answer: "No, subscriptions are non-refundable. Please choose your plan carefully."
    },
    {
      question: "What is the minimum age requirement to use this platform?",
      answer: "One must be at least 18 years old to access this platform."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow pt-32">
        <section className="bg-gradient-to-br from-pale-green/20 to-white relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 bg-[url('/bg-pattern.svg')] opacity-5"></div>
          <div className="absolute right-0 top-0 w-1/3 h-1/3 bg-gradient-to-br from-soft-saffron/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute left-0 bottom-0 w-1/3 h-1/3 bg-gradient-to-tr from-forest-green/10 to-transparent rounded-full blur-3xl"></div>

          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="max-w-6xl mx-auto"
            >
              {/* Breadcrumb Navigation */}
              <motion.div variants={itemVariants} className="mb-4 sm:mb-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-soft-saffron/10 border border-soft-saffron/20">
                  <Info className="w-3 h-3 sm:w-4 sm:h-4 text-soft-saffron" />
                  <span className="text-forest-green/80 text-xs sm:text-sm">
                    <a
                      href="/"
                      className="hover:text-soft-saffron transition-colors"
                    >
                      Home
                    </a>
                    <span className="mx-2">•</span>
                    <span className="text-soft-saffron">FAQ</span>
                  </span>
                </div>
              </motion.div>

              {/* Section Header */}
              <motion.div variants={itemVariants} className="text-center mb-16">
                {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-soft-saffron/10 border border-soft-saffron/20 mb-6">
                  <HelpCircle className="w-4 h-4 text-soft-saffron" />
                  <span className="text-sm font-medium text-forest-green">Frequently Asked Questions</span>
                </div> */}
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest-green mb-6">
                  Common Questions About <span className="text-soft-saffron">Yoga Nidra</span>
                </h1>
                <p className="text-lg text-copper-brown/80 max-w-2xl mx-auto">
                  Find answers to commonly asked questions about Yoga Nidra practice and its benefits.
                </p>
              </motion.div>

              {/* FAQ Accordion */}
              <motion.div variants={itemVariants} className="space-y-4">
                <Accordion type="single" collapsible className="w-full space-y-6 mb-2">
                  {faqs.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-white rounded-lg border border-gray-200 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] transition-all duration-300"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left font-medium text-forest-green hover:text-soft-saffron hover:underline transition-colors duration-300">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 text-copper-brown">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FAQPage;
